import { Tooltip } from '@pique/component';
import { memo, useCallback, useEffect, useMemo } from 'react';

import {
  generateAvatarUrl,
  getColorFromName,
  getInitials,
} from '../../../modules/properties/services/attendee-id-utils';
import type { SerializedAttendee } from '../../../modules/properties/services/schema';
import * as styles from './attendee-avatars.css';

export interface Attendee {
  id?: string;
  name?: string;
  email: string;
  displayName?: string | null;
  avatar?: string | null;
}

export interface AttendeeAvatarsProps {
  attendees: Attendee[];
  maxDisplay?: number;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  showTooltips?: boolean;
  selectable?: boolean;
  selected?: Set<string> | string[];
  currentUserEmail?: string;
  onToggle?: (email: string, selected: boolean, attendee: Attendee) => void;
  onSelectionChange?: (info: {
    emails: string[];
    setId: string;
    count: number;
  }) => void;
}

export { generateAvatarUrl as getAvatarUrl };

interface AttendeeAvatarItemProps {
  attendee: Attendee;
  index: number;
  totalCount: number;
  size: 'small' | 'medium' | 'large';
  marginOffset: string;
  showTooltips: boolean;
  selectable: boolean;
  isSelected: boolean;
  onToggle?: (attendee: Attendee) => void;
}

const AttendeeAvatarItem = memo(function AttendeeAvatarItem({
  attendee,
  index,
  totalCount,
  size,
  marginOffset,
  showTooltips,
  selectable,
  isSelected,
  onToggle,
}: AttendeeAvatarItemProps) {
  const name =
    attendee.name ||
    attendee.displayName ||
    (attendee.email ? attendee.email.split('@')[0] : undefined) ||
    '?';
  const initials = getInitials(name);
  const backgroundColor = getColorFromName(name);
  const avatarUrl = generateAvatarUrl(name, attendee.avatar);

  const handleClick = useCallback(() => {
    onToggle?.(attendee);
  }, [onToggle, attendee]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggle?.(attendee);
      }
    },
    [onToggle, attendee]
  );

  const avatarNode = (
    <div
      className={styles.attendeeAvatar}
      style={{
        backgroundColor: avatarUrl ? undefined : backgroundColor,
        backgroundImage: avatarUrl ? `url('${avatarUrl}')` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        zIndex: totalCount - index,
        marginLeft: index > 0 ? marginOffset : '0',
        outline:
          selectable && isSelected
            ? '2px solid var(--pique-green-primary)'
            : undefined,
        boxShadow:
          selectable && isSelected
            ? '0 0 0 2px var(--pique-green-border)'
            : undefined,
        cursor: selectable ? 'pointer' : undefined,
      }}
      data-size={size}
      role={selectable ? 'button' : undefined}
      aria-pressed={selectable ? (isSelected ? 'true' : 'false') : undefined}
      tabIndex={selectable ? 0 : undefined}
      onClick={selectable ? handleClick : undefined}
      onKeyDown={selectable ? handleKeyDown : undefined}
      title={showTooltips ? undefined : attendee.email}
    >
      {!avatarUrl && initials}
      {selectable && isSelected ? (
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          style={{
            position: 'absolute',
            right: -2,
            bottom: -2,
            background: 'var(--pique-green-primary)',
            borderRadius: '50%',
            border: '1.5px solid white',
          }}
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M20 6L9 17L4 12"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
    </div>
  );

  return showTooltips ? (
    <Tooltip
      key={attendee.id || attendee.email}
      content={attendee.email}
      side="top"
    >
      {avatarNode}
    </Tooltip>
  ) : (
    avatarNode
  );
});

export const AttendeeAvatars = memo(function AttendeeAvatars({
  attendees,
  maxDisplay = 3,
  size = 'medium',
  className,
  showTooltips = true,
  selectable = false,
  selected,
  currentUserEmail,
  onToggle,
  onSelectionChange,
}: AttendeeAvatarsProps) {
  const selectedSet = useMemo(() => {
    if (!selected) return null;
    const toSet = (arr: string[]) =>
      new Set(arr.map(s => s.trim().toLowerCase()));
    return selected instanceof Set
      ? toSet(Array.from(selected))
      : toSet(selected);
  }, [selected]);
  const selectedEmails = useMemo((): string[] => {
    if (!selectedSet) return [];
    return Array.from(selectedSet).sort();
  }, [selectedSet]);

  useEffect(() => {
    if (!onSelectionChange) return;
    const setId = selectedEmails.join(',');
    onSelectionChange({
      emails: selectedEmails,
      setId,
      count: selectedEmails.length,
    });
  }, [onSelectionChange, selectedEmails]);

  const handleToggleItem = useCallback(
    (a: Attendee) => {
      const key = (a.email || '').trim().toLowerCase();
      const isSel = selectedSet ? selectedSet.has(key) : false;

      if (onToggle) {
        onToggle(a.email, !isSel, a);
      }

      if (onSelectionChange) {
        const next = new Set(selectedSet || []);
        if (isSel) next.delete(key);
        else next.add(key);
        const emails = Array.from(next).sort();
        const setId = emails.join(',');
        onSelectionChange({ emails, setId, count: emails.length });
      }
    },
    [onToggle, onSelectionChange, selectedSet]
  );

  const normalizedCurrent = (currentUserEmail || '').trim().toLowerCase();
  const filteredAttendees = normalizedCurrent
    ? attendees.filter(
        a => (a.email || '').trim().toLowerCase() !== normalizedCurrent
      )
    : attendees;
  if (!filteredAttendees || filteredAttendees.length === 0) {
    return null;
  }

  const displayAttendees = filteredAttendees.slice(0, maxDisplay);
  const remainingCount = Math.max(0, filteredAttendees.length - maxDisplay);
  const marginOffset = '-6px';

  return (
    <div
      className={`${styles.attendeeStack} ${className || ''}`}
      data-size={size}
    >
      {displayAttendees.map((attendee, index) => (
        <AttendeeAvatarItem
          key={attendee.id || attendee.email}
          attendee={attendee}
          index={index}
          totalCount={displayAttendees.length}
          size={size}
          marginOffset={marginOffset}
          showTooltips={showTooltips}
          selectable={selectable}
          isSelected={
            selectedSet?.has((attendee.email || '').trim().toLowerCase()) ??
            false
          }
          onToggle={handleToggleItem}
        />
      ))}
      {remainingCount > 0 && (
        <div
          className={styles.attendeeCountBadge}
          style={{ marginLeft: marginOffset, zIndex: 0 }}
          data-size={size}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  );
});

// Helper function to adapt SerializedAttendee to Attendee interface
export const adaptSerializedAttendees = (
  attendees: SerializedAttendee[]
): Attendee[] => {
  return attendees.map(attendee => ({
    id: attendee.id,
    name: attendee.name,
    email: attendee.email,
    avatar: attendee.avatar,
  }));
};
